import type {
  ExtensionConfig,
  isMarkActive as isMarkActiveFn,
  MarkConfig,
  markInputRule as markInputRuleFn,
  markPasteRule as markPasteRuleFn,
  mergeAttributes as mergeAttributesFn,
  NodeConfig,
  NodeViewRendererProps,
  textblockTypeInputRule as textblockTypeInputRuleFn,
} from '@tiptap/core';
import type { Node as ProsemirrorNode } from '@tiptap/pm/model';
import type { Plugin as IPlugin, PluginKey as IPluginKey, Selection } from '@tiptap/pm/state';
import type { Editor, JSONContent, NodeViewContent } from '@tiptap/react';
import type { ComponentType } from 'react';
import type { ComponentData } from 'ricos-content';
import type { Decoration_Type, DocumentStyle } from 'ricos-schema';

import type { LinkSettings, RicosEditorAPI, RicosServices, RicosTheme, TextAlignment } from '.';
import type { TFunction } from './commonTypes';
import type { EditorPlugin, LegacyEditorPluginConfig, Node_Type } from './pluginTypes';

export type PluginProps = NodeViewRendererProps & {
  settings: LegacyEditorPluginConfig;
  componentData: ComponentData;
  updateAttributes: (data: unknown) => null;
  selected: boolean;
  NodeViewContent: typeof NodeViewContent;
};

export type RicosNodeProps = NodeViewRendererProps &
  RicosTiptapContextValue & {
    componentData: NodeViewRendererProps['node']['attrs'];
  };

export type TiptapExtensionConfig = NodeConfig | MarkConfig | RicosExtensionConfig;

export const isNodeConfig = (c: TiptapExtensionConfig): c is NodeConfig => c.type === 'node';
export const isMarkConfig = (c: TiptapExtensionConfig): c is MarkConfig => c.type === 'mark';
export const isExtensionConfig = (c: TiptapExtensionConfig): c is RicosExtensionConfig =>
  c.type === 'extension';

export type NodeHoc<T = any> = (Component: ComponentType<T>) => ComponentType<T>;

export type NodeViewHocMap = {
  [type: string]: NodeHoc[];
};

export type RicosTiptapContextValue = {
  context: {
    t?: TFunction;
  };
};

export interface RicosExtensionConfig<Options = any, Storage = any>
  extends ExtensionConfig<Options, Storage> {
  addNodeHoc?: () => NodeHocDescriptor;
}

export type NodeHocDescriptor = {
  nodeTypes: string[];
  nodeHoc: NodeHoc;
  priority: number;
};

export type Group =
  | 'text'
  | 'spoilerable'
  | 'shortcuts-enabled'
  | 'overlay'
  | 'custom-styles'
  | 'resizable'
  | 'draggable'
  | 'no-trailing-node'
  | 'node-container'
  | 'footer-disabled'
  | 'hide-selection'
  | 'not-overtypable';

export type RicosNodeExtension<Options = any, Storage = any> =
  | ReactRicosNodeExtension<Options, Storage>
  | HtmlRicosNodeExtension<Options, Storage>;

export type RicosNodeExtensionBase<NodeType, Options = any, Storage = any> = {
  name: Node_Type | string;
  groups: Group[];
  settings?: Record<string, unknown>;
  reconfigure?: (
    this: Exclude<
      RicosNodeExtensionBase<NodeType, Options, Storage>,
      'reconfigure' | 'createExtensionConfig'
    >,
    config: NodeConfig<Options, Storage>,
    extensions: RicosExtension[],
    ricosProps: ExtensionProps,
    settings: Record<string, unknown>,
    services: RicosServices
  ) => NodeConfig<Options, Storage>;
  createExtensionConfig: (
    this: Exclude<
      RicosNodeExtensionBase<NodeType, Options, Storage>,
      'reconfigure' | 'createExtensionConfig'
    >,
    {
      textblockTypeInputRule,
      mergeAttributes,
      markPasteRule,
      markInputRule,
      Plugin,
      PluginKey,
    }: {
      textblockTypeInputRule: typeof textblockTypeInputRuleFn;
      mergeAttributes: typeof mergeAttributesFn;
      markPasteRule: typeof markPasteRuleFn;
      markInputRule: typeof markInputRuleFn;
      Plugin: typeof IPlugin;
      PluginKey: typeof IPluginKey;
    }
  ) => NodeConfig<Options, Storage>;
  placeholder?: {
    predicate: (PlaceholderProps: {
      doc: ProsemirrorNode;
      node: ProsemirrorNode;
      selection: Selection;
      pos: number;
    }) => boolean;
    content: string;
    alwaysVisible?: boolean;
  };
} & NodeType;

type ReactRicosNode = {
  type: 'react-node';
  Component: ComponentType<PluginProps>;
};

export type ReactRicosNodeExtension<Options = any, Storage = any> = RicosNodeExtensionBase<
  ReactRicosNode,
  Options,
  Storage
>;

type HTMLRicosNode = {
  type: 'html-node';
};

export type HtmlRicosNodeExtension<Options = any, Storage = any> = RicosNodeExtensionBase<
  HTMLRicosNode,
  Options,
  Storage
>;

export type Mark_Type =
  | Exclude<`${Decoration_Type}`, 'COLOR'>
  | 'COLOR_BACKGROUND'
  | 'COLOR_FOREGROUND'
  | 'HASHTAG';

// eslint-disable-next-line no-redeclare
export const Mark_Type: { [key in Mark_Type]: Mark_Type } = {
  ANCHOR: 'ANCHOR',
  BOLD: 'BOLD',
  COLOR_BACKGROUND: 'COLOR_BACKGROUND',
  COLOR_FOREGROUND: 'COLOR_FOREGROUND',
  EXTERNAL: 'EXTERNAL',
  FONT_SIZE: 'FONT_SIZE',
  HASHTAG: 'HASHTAG',
  ITALIC: 'ITALIC',
  LINK: 'LINK',
  MENTION: 'MENTION',
  SPOILER: 'SPOILER',
  UNDERLINE: 'UNDERLINE',
  UNRECOGNIZED: 'UNRECOGNIZED',
};

export type RicosMarkExtension<Options = any, Storage = any> = {
  name: Mark_Type;
  type: 'mark';
  groups: Group[];
  settings?: Record<string, unknown>;
  reconfigure?: (
    this: Exclude<RicosMarkExtension<Options, Storage>, 'reconfigure' | 'createExtensionConfig'>,
    config: MarkConfig<Options, Storage>,
    extensions: RicosExtension[],
    ricosProps: ExtensionProps,
    settings: Record<string, unknown>,
    services: RicosServices
  ) => MarkConfig<Options, Storage>;
  createExtensionConfig: (
    this: Exclude<RicosMarkExtension<Options, Storage>, 'reconfigure' | 'createExtensionConfig'>,
    {
      isMarkActive,
      textblockTypeInputRule,
      mergeAttributes,
      markPasteRule,
      markInputRule,
      Plugin,
      PluginKey,
    }: {
      isMarkActive: typeof isMarkActiveFn;
      textblockTypeInputRule: typeof textblockTypeInputRuleFn;
      mergeAttributes: typeof mergeAttributesFn;
      markPasteRule: typeof markPasteRuleFn;
      markInputRule: typeof markInputRuleFn;
      Plugin: typeof IPlugin;
      PluginKey: typeof IPluginKey;
    }
  ) => MarkConfig<Options, Storage>;
};

export type RicosFunctionalExtension<Options = any, Storage = any> = {
  name: string;
  type: 'extension';
  groups: Group[];
  placeholder?: {
    predicate: (PlaceholderProps: {
      doc: ProsemirrorNode;
      node: ProsemirrorNode;
      selection: Selection;
      pos: number;
    }) => boolean;
    content: string;
    alwaysVisible?: boolean;
  };
  settings?: Record<string, unknown>;
  reconfigure?: (
    this: Exclude<
      RicosFunctionalExtension<Options, Storage>,
      'reconfigure' | 'createExtensionConfig'
    >,
    config: RicosExtensionConfig<Options, Storage>,
    extensions: RicosExtension[],
    ricosProps: ExtensionProps,
    settings: Record<string, unknown>,
    services: RicosServices
  ) => RicosExtensionConfig<Options, Storage>;
  createExtensionConfig: (
    this: Exclude<
      RicosFunctionalExtension<Options, Storage>,
      'reconfigure' | 'createExtensionConfig'
    >,
    {
      mergeAttributes,
    }: {
      mergeAttributes: typeof mergeAttributesFn;
    }
  ) => RicosExtensionConfig<Options, Storage>;
};

export const isRicosNodeExtension = (ext: RicosExtension): ext is RicosNodeExtension =>
  ext.type === 'react-node' || ext.type === 'html-node';
export const isRicosMarkExtension = (ext: RicosExtension): ext is RicosMarkExtension =>
  ext.type === 'mark';
export const isRicosFunctionalExtension = (ext: RicosExtension): ext is RicosFunctionalExtension =>
  ext.type === 'extension';

export type RicosExtension<Options = any, Storage = any> =
  | RicosNodeExtension<Options, Storage>
  | RicosMarkExtension<Options, Storage>
  | RicosFunctionalExtension<Options, Storage>;

export interface TiptapEditorPlugin extends EditorPlugin {
  tiptapExtensions: RicosExtension[];
}

export type { DOMOutputSpec } from '@tiptap/pm/model';
export { MarkConfig, NodeConfig };

export type ExtensionProps = {
  placeholder?: string;
  textAlignment?: TextAlignment;
  iframeSandboxDomain?: string;
  isTextWrap?: boolean;
  maxTextLength?: number;
  anchorTarget?: LinkSettings['anchorTarget'];
  rel?: LinkSettings['rel'];
  relValue?: LinkSettings['relValue'];
  breakoutRule?: NonNullable<RicosTheme['breakout']>['rule'];
};

export type HtmlAttributes = {
  autoCapitalize: string;
  /**
   * true/false enumeration as required by the HTML spec
   * @default: true
   */
  spellCheck: 'true' | 'false';
  autoComplete: string;
  autoCorrect: string;
  tabIndex: number;
};

export type TiptapEditor = Editor;

export interface TiptapAdapter {
  getEditorCommands: RicosEditorAPI['getEditorCommands'];
  getToolbarProps: RicosEditorAPI['getToolbarProps'];
  focus: RicosEditorAPI['focus'];
  blur: RicosEditorAPI['blur'];
  tiptapEditor: TiptapEditor;
  getDocumentStyle: () => DocumentStyle;
  setTiptapContent: (content: JSONContent) => void;
}
